import React, { useEffect, useRef, useState } from 'react';
import { Button, Col, Form } from "react-bootstrap";
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import '@ckeditor/ckeditor5-build-classic/build/translations/it';
import { InfoCircle, SearchNormal } from 'iconsax-react';

/**
 * I pezzi di interfaccia della scheda assemblea che valgono da soli.
 *
 * Stanno qui e non in dettaglio.js perché quel file ha già il suo lavoro da fare
 * (lo stato del server e le quattro schede) e perché questi si leggono meglio
 * isolati, con accanto il motivo per cui sono fatti così.
 */

// Un dato piccolo — data, ora, numero — non ha bisogno di un campo lungo quanto la
// colonna che lo ospita.
const LARGHEZZA_MASSIMA = { date: '160px', time: '110px', number: '90px' };

export const Campo = ({ id = undefined, label, obbligatorio = false, valore, type = 'text', disabled = false, onChange = null, md = 6, flex = false, larghezza = null, nota = null }) => {
	const Wrapper = flex ? 'div' : Col;
	// Larghezza fissa anche da bloccato: se dipendesse dal contenuto, righe con
	// valori di lunghezza diversa finirebbero disallineate fra loro.
	const maxWidth = larghezza || LARGHEZZA_MASSIMA[type];
	const wrapperProps = flex
		? { className: 'mb-3', style: maxWidth ? { width: maxWidth, flex: `0 0 ${maxWidth}` } : { flex: '1 1 220px' } }
		: { md, className: 'mb-3' };

	return (
		<Wrapper {...wrapperProps}>
			<Form.Label htmlFor={id}>{label}{obbligatorio && !disabled ? ' *' : ''}</Form.Label>
			{disabled
				? <div className="sid-asm-dato-bloccato">{valore || '—'}</div>
				: (
					<Form.Control
						id={id}
						type={type}
						value={valore}
						onChange={(e) => onChange && onChange(e.target.value)}
						style={{ maxWidth: '100%' }}
					/>
				)}
			{/* Sotto il campo, non nell'etichetta: in un'etichetta corta come "Orario
			    fine" andrebbe a capo e disallineerebbe la riga rispetto alle altre. */}
			{nota && !disabled && <div className="sid-asm-campo-nota">{nota}</div>}
		</Wrapper>
	);
};

// Sotto le tre righe non vale la pena troncare: si vede già tutto.
const supera3Righe = (html) => (html || '').replace(/<[^>]+>/g, ' ').trim().length > 200;

/**
 * Il testo libero delle quattro sezioni in WYSIWYG.
 *
 * Quando non è modificabile non ha senso mostrare una toolbar inutilizzabile: si
 * passa a testo in sola lettura, troncato a tre righe con "Leggi tutto" se è lungo.
 *
 * La toolbar è ristretta alle marcature che il renderer PDF sa impaginare
 * (@libs/pdf-html): grassetto, corsivo, titoli, elenchi. Un colore o una tabella
 * finirebbero nel verbale a schermo e sparirebbero dal PDF, che è il documento che
 * poi vale.
 */
export const TestoLibero = ({ valore, onChange, disabled }) => {
	const [espanso, setEspanso] = useState(false);

	if (disabled) {
		const lungo = supera3Righe(valore);
		return (
			<div>
				<div
					className={`sid-asm-testo-locked${lungo && !espanso ? ' sid-asm-testo-locked--chiuso' : ''}`}
					dangerouslySetInnerHTML={{ __html: valore || '<p class="text-muted">—</p>' }}
				/>
				{lungo && (
					<div className="d-flex justify-content-end mt-2">
						<Button variant="link" className="p-0" style={{ fontSize: '12.5px', textTransform: 'none' }} onClick={() => setEspanso(e => !e)}>
							{espanso ? 'Mostra meno' : 'Leggi tutto'}
						</Button>
					</div>
				)}
			</div>
		);
	}

	return (
		<div className="custom-ekeditor">
			<CKEditor
				editor={ClassicEditor}
				data={valore}
				config={{
					language: 'it',
					toolbar: [
						'heading', '|', 'bold', 'italic', 'underline', '|',
						'bulletedList', 'numberedList', '|', 'undo', 'redo'
					]
				}}
				onChange={(event, editor) => onChange(editor.getData())}
			/>
		</div>
	);
};

/**
 * Quando uno stato non permette di usare una sezione, il motivo va scritto sopra.
 * `sfumato` mette in trasparenza anche il contenuto: utile dove sotto c'è un
 * modulo che non torna più utile leggere, non dove c'è testo ancora da consultare.
 */
export const SezioneBloccata = ({ bloccata, messaggio, sfumato = true, children }) => (
	<>
		{bloccata && messaggio && (
			<div className="sid-asm-banner">
				<InfoCircle size="16" className="me-1" /> {messaggio}
			</div>
		)}
		<div className={bloccata && sfumato ? 'sid-asm-locked' : undefined}>
			{children}
		</div>
	</>
);

/** Pager minimo: Precedente, pagina corrente, Successiva. Niente di più. */
export const Paginazione = ({ pagina, totalePagine, onCambiaPagina }) => {
	if (totalePagine <= 1) return null;

	return (
		<div className="d-flex align-items-center justify-content-end gap-2 mt-2">
			<Button
				className="btn btn-add sid-btn-secondary" style={{ textTransform: 'none' }}
				disabled={pagina <= 1} onClick={() => onCambiaPagina(pagina - 1)}
			>
				Precedente
			</Button>
			<span className="text-muted" style={{ fontSize: '13px' }}>Pagina {pagina} di {totalePagine}</span>
			<Button
				className="btn btn-add sid-btn-secondary" style={{ textTransform: 'none' }}
				disabled={pagina >= totalePagine} onClick={() => onCambiaPagina(pagina + 1)}
			>
				Successiva
			</Button>
		</div>
	);
};

/**
 * Campo con ricerca al posto di una tendina.
 *
 * Serve a scegliere delegante e delegato fra i convocati. Una `<select>` piena
 * richiederebbe di avere in mano tutto l'elenco, che è esattamente quello che la
 * paginazione lato server evita: qui si cercano invece dal server, un pugno alla
 * volta, e con qualche centinaio di soci è anche più comodo da usare.
 */
export const CampoConRicerca = ({ label, valore, onSeleziona, cerca, placeholder, disabled }) => {
	const [testo, setTesto] = useState('');
	const [risultati, setRisultati] = useState([]);
	const [aperto, setAperto] = useState(false);
	const contenitore = useRef(null);

	useEffect(() => {
		if (!aperto) return;
		const timer = setTimeout(async () => {
			const trovati = await cerca(testo.trim());
			setRisultati(trovati || []);
		}, 350);
		return () => clearTimeout(timer);
	}, [testo, aperto, cerca]);

	// Un click fuori chiude l'elenco: senza, resterebbe aperto sopra il resto del
	// modulo mentre si compila il campo successivo.
	useEffect(() => {
		const fuori = (e) => {
			if (contenitore.current && !contenitore.current.contains(e.target)) setAperto(false);
		};
		document.addEventListener('mousedown', fuori);
		return () => document.removeEventListener('mousedown', fuori);
	}, []);

	return (
		<div ref={contenitore} style={{ position: 'relative', minWidth: '220px' }}>
			<Form.Label style={{ fontSize: '12px' }}>{label}</Form.Label>
			<div className="sid-search">
				<SearchNormal size={16} className="sid-search__icon" />
				<input
					className="sid-search__input"
					placeholder={placeholder}
					disabled={disabled}
					value={aperto ? testo : (valore?.name || '')}
					onFocus={() => { setAperto(true); setTesto(''); }}
					onChange={(e) => setTesto(e.target.value)}
				/>
			</div>
			{aperto && (
				<div className="sid-asm-suggerimenti">
					{risultati.length === 0 && <div className="sid-asm-suggerimento text-muted">Nessun risultato</div>}
					{risultati.map(socio => (
						<button
							key={socio.userId}
							type="button"
							className="sid-asm-suggerimento"
							onClick={() => { onSeleziona(socio); setAperto(false); }}
						>
							{socio.name}
						</button>
					))}
				</div>
			)}
		</div>
	);
};

/** `{ content, name }` in base64, la convenzione degli upload di tutto il sistema. */
export const leggiFile = (file) => new Promise((resolve, reject) => {
	const reader = new FileReader();
	reader.onerror = reject;
	// readAsDataURL produce "data:<mime>;base64,<contenuto>": al backend serve
	// solo la seconda parte.
	reader.onload = () => resolve({ content: String(reader.result).split(',')[1], name: file.name });
	reader.readAsDataURL(file);
});

/** Scarica un blob dando al file il nome giusto, senza lasciare l'url in giro. */
export const scaricaBlob = (blob, nome) => {
	const url = window.URL.createObjectURL(blob);
	const link = document.createElement('a');
	link.href = url;
	link.setAttribute('download', nome);
	document.body.appendChild(link);
	link.click();
	link.remove();
	window.URL.revokeObjectURL(url);
};
